// The office register-confirmation gate (Session AIG, "Renewal A"). Before any renewal link can
// exist (a later session), the office confirms the covered lift register -- count, stops, type, age
// -- exactly as it would be used to reprice. A flagged field still carries a value written by the
// V1-to-V2 migration with no correction recorded in V2 since (the ADZ session found the migration
// had loaded bad data into at least one lift's type field undetected for months). Casandra can
// confirm as-is or correct a field inline: a correction is a NORMAL lift edit through the SAME
// PATCH /api/assets/:id the Lifts register uses (src/lib/resources.ts `assets`) -- nothing new is
// built for the write path here, deliberately, so this can never drift from the register itself.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type ContractRegisterReview, type RegisterReviewField } from "../../lib/api";
import { assets } from "../../lib/resources";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { buttonClasses } from "../../components/Button";
import { formatDate } from "../../lib/format";

const FIELD_LABEL: Record<RegisterReviewField, string> = {
  liftType: "Type",
  stops: "Stops",
  yearOfManufacture: "Year built",
};
const FIELD_ORDER: RegisterReviewField[] = ["liftType", "stops", "yearOfManufacture"];

function EditableField({
  liftId,
  field,
  value,
  flagged,
  onSaved,
}: {
  liftId: string;
  field: RegisterReviewField;
  value: string | number | null;
  flagged: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setDraft(value == null ? "" : String(value));
    setError(null);
    setEditing(true);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const trimmed = draft.trim();
      const payload: Record<string, unknown> =
        field === "liftType" ? { liftType: trimmed === "" ? null : trimmed } : { [field]: trimmed === "" ? null : Number(trimmed) };
      // The existing register PATCH route, nothing new for the write path (spec constraint).
      await assets.update(liftId, payload);
      setEditing(false);
      onSaved(); // re-read the review from the server: the flag itself is a server-derived fact
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save that change.");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          type={field === "liftType" ? "text" : "number"}
          aria-label={`${FIELD_LABEL[field]} for this lift`}
          className="max-sm:min-h-11 w-24 rounded border border-ele-purple-accent bg-ele-bg px-1.5 py-0.5 text-sm text-ele-strong"
          autoFocus
        />
        <button onClick={() => void save()} disabled={busy} className="max-sm:min-h-11 text-xs text-ele-green hover:underline disabled:opacity-50">
          {busy ? "Saving..." : "Save"}
        </button>
        <button onClick={() => setEditing(false)} disabled={busy} className="max-sm:min-h-11 text-xs text-ele-body hover:underline">
          Cancel
        </button>
        {error && <span role="alert" className="text-xs text-ele-danger">{error}</span>}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={flagged ? "rounded bg-amber-400/15 px-1.5 py-0.5 text-amber-400" : "text-ele-strong"}>
        {value == null || value === "" ? "Not set" : value}
      </span>
      {flagged && (
        <span className="text-[10px] uppercase tracking-wide text-amber-400" title="Unedited value from the V1 migration: check before confirming">
          migrated
        </span>
      )}
      <button onClick={startEdit} className="max-sm:min-h-11 text-xs text-ele-body underline-offset-2 hover:text-ele-green hover:underline">
        Correct
      </button>
    </span>
  );
}

export function RegisterConfirmationPanel({ contractId }: { contractId: string }) {
  const [review, setReview] = useState<ContractRegisterReview | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      setReview(await api.contracts.registerReview(contractId));
      setState("ready");
    } catch {
      setState("error");
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmRegister() {
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      setReview(await api.contracts.confirmRegisterReview(contractId));
    } catch (err) {
      setConfirmError(err instanceof ApiError ? err.message || err.code : "Could not confirm the register. Please try again.");
    } finally {
      setConfirmBusy(false);
    }
  }

  if (state === "error") {
    return (
      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Register confirmation</h2>
        <p role="alert" className="text-sm text-ele-danger">Could not load the register review.</p>
        <button
          onClick={() => void load()}
          className="mt-2 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
        >
          Retry
        </button>
      </section>
    );
  }

  if (state === "loading" || !review) {
    return (
      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Register confirmation</h2>
        <Loader shape="inline" />
      </section>
    );
  }

  const flaggedCount = review.lifts.reduce((n, l) => n + FIELD_ORDER.filter((f) => l.flags[f]).length, 0);

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg text-ele-strong">Register confirmation</h2>
        {review.registerConfirmedAt ? (
          <span className="text-xs text-ele-green">
            Confirmed {formatDate(review.registerConfirmedAt)}
            {review.registerConfirmedByName ? ` by ${review.registerConfirmedByName}` : ""}
          </span>
        ) : (
          <span className="text-xs text-amber-400">Not yet confirmed</span>
        )}
      </div>

      <p className="mb-3 text-sm text-ele-body">
        The lift register behind this contract's renewal reprice, exactly as it would be used to price a renewal.
        {flaggedCount > 0
          ? ` ${flaggedCount} field${flaggedCount === 1 ? "" : "s"} still carries an unedited value from the historical import: check before confirming.`
          : " No field here still carries an unedited migration value."}
      </p>

      {review.lifts.length === 0 ? (
        <p className="text-sm text-ele-body">No lifts are currently covered by this contract.</p>
      ) : (
        <ul className="space-y-2">
          {review.lifts.map((lift) => (
            <li key={lift.id} className="flex flex-col gap-1 border-b border-ele-hairline-soft py-2 last:border-b-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
              <Link to={`/office/lifts/${encodeURIComponent(lift.id)}`} className="ele-ref shrink-0 underline-offset-2 hover:underline">
                {lift.eleNumber ?? "Lift"}
              </Link>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs uppercase tracking-wide text-ele-body">{FIELD_LABEL.liftType}</span>
                  <EditableField liftId={lift.id} field="liftType" value={lift.liftType} flagged={lift.flags.liftType} onSaved={() => void load()} />
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs uppercase tracking-wide text-ele-body">{FIELD_LABEL.stops}</span>
                  <EditableField liftId={lift.id} field="stops" value={lift.stops} flagged={lift.flags.stops} onSaved={() => void load()} />
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs uppercase tracking-wide text-ele-body">{FIELD_LABEL.yearOfManufacture}</span>
                  <EditableField liftId={lift.id} field="yearOfManufacture" value={lift.yearOfManufacture} flagged={lift.flags.yearOfManufacture} onSaved={() => void load()} />
                  {lift.ageBand && <span className="text-xs text-ele-body">({lift.ageBand} yrs)</span>}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {confirmError && <p role="alert" className="mt-2 text-sm text-ele-danger">{confirmError}</p>}

      <div className="mt-3 border-t border-ele-hairline-soft pt-3">
        <ConfirmButton
          onConfirm={() => void confirmRegister()}
          busy={confirmBusy}
          busyLabel="Confirming..."
          label={review.registerConfirmedAt ? "Re-confirm register" : "Confirm register"}
          confirmLabel="Press again to confirm"
          className={buttonClasses("primary", "sm")}
          armedClassName="font-semibold ring-2 ring-ele-blue"
        />
      </div>
    </section>
  );
}
